import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import formatMessage from 'format-message'
import DrawerHeader from './DrawerHeader'
import { Button } from 'react-bootstrap'
import { createAIToolsElement } from '../utils/aiToolsLoader'

/**
 * DrawerSettings - Settings component for the drawer with sync progress
 */
const DrawerSettings = ({ coursenum, userEmail, assistantEnabled, userId }) => {
  const hasUser = !!(userEmail && String(userEmail).trim())

  const [syncStatus, setSyncStatus] = useState('Update Progress')
  const [syncInProgress, setSyncInProgress] = useState(false)
  const [optionFields, setOptionFields] = useState([])
  const [loadingAdvancedOptions, setLoadingAdvancedOptions] = useState(false)
  const [aiSettingsLoaded, setAiSettingsLoaded] = useState(false)
  const aiSettingsContainerRef = useRef(null)
  const aiSettingsElementRef = useRef(null)
  const isMountedRef = useRef(true)

  const STATUS_TEXT = [
    'Updated',
    'Update Progress',
    'Updating...',
    'Update Error',
  ]

  const handleSyncProgress = () => {
    if (window.MyEd_syncAnalytics) {
      window.MyEd_syncAnalytics(true)
    } else {
      setSyncInProgress(true)
      setSyncStatus('Updating...')

      setTimeout(() => {
        setSyncStatus('Updated')
        setSyncInProgress(false)

        setTimeout(() => {
          setSyncStatus('Update Progress')
        }, 30000)
      }, 1500)
    }
  }

  const loadOptionFields = async () => {
    if (!coursenum || loadingAdvancedOptions) return
    setLoadingAdvancedOptions(true)
    try {
      const data = await $.ajax({
        url: `/reader/booktools.advanced_options_schema/${coursenum}/`,
        type: 'GET',
        dataType: 'json',
      })
      const { fields = [] } = data || {}
      setOptionFields(fields)
    } catch (error) {
      console.error('Failed to load option fields:', error)
      setOptionFields([])
    } finally {
      setLoadingAdvancedOptions(false)
    }
  }

  const handleOptionChange = (fieldName, newValue) => {
    const values = Array.isArray(newValue) ? newValue : [newValue]

    $.ajax({
      url: `/reader/booktools.save_advanced_option/${coursenum}/`,
      type: 'POST',
      contentType: 'application/json',
      headers: (function () {
        const m = document.cookie.match(/(?:^|; )csrftoken=([^;]+)/)
        const headers = { 'X-Requested-With': 'XMLHttpRequest' }
        if (m) headers['X-CSRFToken'] = decodeURIComponent(m[1])
        return headers
      })(),
      data: JSON.stringify({ name: fieldName, values }),
    })
      .done(() => {
        window.location.reload()
      })
      .fail((xhr, status, error) => {
        console.error('Failed to save option', fieldName, error)
      })
  }

  const renderField = field => {
    const {
      name,
      type,
      label,
      options = [],
      value = [],
      multiple,
      placeholder,
      rows,
    } = field
    const currentValue = Array.isArray(value) ? value : [value].filter(Boolean)

    switch (type) {
      case 'radio':
        return (
          <div key={name} className="course-options-field">
            <div className="course-options-field-header">{label}</div>
            <div className="course-options-container">
              {options.map(option => (
                <label key={option.value} className="course-options-item">
                  <input
                    type="radio"
                    name={name}
                    value={option.value}
                    checked={currentValue.includes(option.value)}
                    onChange={e => handleOptionChange(name, e.target.value)}
                    className="course-options-input"
                  />
                  <span className="course-options-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )

      case 'checkbox':
        return (
          <div key={name} className="course-options-field">
            <div className="course-options-field-header">{label}</div>
            <div className="course-options-container">
              {options.map(option => (
                <label key={option.value} className="course-options-item">
                  <input
                    type="checkbox"
                    name={name}
                    value={option.value}
                    checked={currentValue.includes(option.value)}
                    onChange={e => {
                      const newValues = e.target.checked
                        ? [...currentValue, option.value]
                        : currentValue.filter(v => v !== option.value)
                      handleOptionChange(name, newValues)
                    }}
                    className="course-options-input"
                  />
                  <span className="course-options-label">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        )

      case 'select':
        return (
          <div key={name} className="course-options-field">
            <label className="course-options-field-header">{label}</label>
            <select
              name={name}
              multiple={multiple}
              value={multiple ? currentValue : currentValue[0] || ''}
              onChange={e => {
                const newValue = multiple
                  ? Array.from(e.target.selectedOptions, opt => opt.value)
                  : e.target.value
                handleOptionChange(name, newValue)
              }}
              className="course-options-form-control"
            >
              {!multiple && <option value="">-- Select --</option>}
              {options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )

      case 'textarea':
        return (
          <div key={name} className="course-options-field">
            <label className="course-options-field-header">{label}</label>
            <textarea
              name={name}
              value={currentValue[0] || ''}
              placeholder={placeholder}
              rows={rows || 3}
              onChange={e => handleOptionChange(name, e.target.value)}
              className="course-options-form-control course-options-textarea"
            />
          </div>
        )

      default: // text, number, etc.
        return (
          <div key={name} className="course-options-field">
            <label className="course-options-field-header">{label}</label>
            <input
              type={type}
              name={name}
              value={currentValue[0] || ''}
              placeholder={placeholder}
              onChange={e => handleOptionChange(name, e.target.value)}
              className="course-options-form-control"
            />
          </div>
        )
    }
  }

  useEffect(() => {
    if (!hasUser) {
      return
    }

    window.updateStatusButton = (statusIndex, force) => {
      if (
        typeof statusIndex === 'number' &&
        statusIndex >= 0 &&
        statusIndex < STATUS_TEXT.length
      ) {
        setSyncStatus(STATUS_TEXT[statusIndex])
        setSyncInProgress(statusIndex === 2)
      } else if (force) {
        setSyncStatus('Update Progress')
        setSyncInProgress(false)
      }
    }

    loadOptionFields()

    return () => {
      if (window.updateStatusButton) {
        delete window.updateStatusButton
      }
    }
  }, [coursenum, hasUser])

  useEffect(() => {
    const handleDrawerStateChange = event => {
      const activeDrawer = event && event.detail && event.detail.activeDrawer
      if (activeDrawer === 'settings' && hasUser) {
        setTimeout(() => {
          const settingsBody = document.querySelector(
            '.drawer-settings .drawer-scrollable-content'
          )
          if (settingsBody) {
            const firstFocusable = settingsBody.querySelector(
              'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            )
            if (firstFocusable) {
              firstFocusable.focus()
            }
          }
        }, 400)
      }
    }

    document.addEventListener('drawer-state-change', handleDrawerStateChange)
    return () => {
      document.removeEventListener(
        'drawer-state-change',
        handleDrawerStateChange
      )
    }
  }, [hasUser])

  const createAISettingsElement = async () => {
    if (
      !aiSettingsContainerRef.current ||
      !isMountedRef.current ||
      aiSettingsLoaded
    ) {
      return
    }

    await createAIToolsElement({
      containerRef: aiSettingsContainerRef,
      elementRef: aiSettingsElementRef,
      isMountedRef: isMountedRef,
      forceRecreate: true,
      setElementCreated: setAiSettingsLoaded,
      attributes: {
        tooltype: 'aisettings',
        coursenum: coursenum,
        userid: userId,
      },
    })
  }

  useEffect(() => {
    const handleDrawerOpen = event => {
      const activeDrawer = event && event.detail && event.detail.activeDrawer
      if (
        activeDrawer === 'settings' &&
        assistantEnabled &&
        hasUser &&
        !aiSettingsLoaded
      ) {
        setTimeout(() => {
          createAISettingsElement()
        }, 100)
      }
    }

    document.addEventListener('drawer-state-change', handleDrawerOpen)
    return () => {
      document.removeEventListener('drawer-state-change', handleDrawerOpen)
    }
  }, [assistantEnabled, hasUser, aiSettingsLoaded])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      if (aiSettingsElementRef.current) {
        aiSettingsElementRef.current.remove()
        aiSettingsElementRef.current = null
      }
      setAiSettingsLoaded(false)
    }
  }, [])

  return (
    <div className="drawer-settings">
      <DrawerHeader title={formatMessage('Settings')} />

      <div className="drawer-scrollable-content">
        {!hasUser ? (
          <div className="drawer-empty-state p-3">
            <p className="text-muted text-p-margin-removed">
              {formatMessage(
                'You must be logged in to change settings for this course.'
              )}
            </p>
          </div>
        ) : (
          <div className="settings-options">
            {(loadingAdvancedOptions ||
              (optionFields && optionFields.length > 0)) && (
              <span>
                <div className="settings-section">
                  <h3 className="settings-header-title">
                    {formatMessage('Course Options')}
                  </h3>
                  {loadingAdvancedOptions ? (
                    <div className="text-center py-3">
                      <i
                        className="fa fa-spinner fa-spin"
                        aria-hidden="true"
                      ></i>
                      <span className="ml-2">
                        {formatMessage('Loading course options...')}
                      </span>
                    </div>
                  ) : (
                    <div className="advanced-options-content">
                      {optionFields.map(renderField)}
                    </div>
                  )}
                </div>
              </span>
            )}
          </div>
        )}

        {assistantEnabled && (
          <React.Fragment>
            <div className="settings-section">
              <h3 className="settings-header-title">
                {formatMessage('AI Assistant Settings')}
              </h3>
              {!aiSettingsLoaded && (
                <div className="ai-settings-skeleton">
                  <div className="skeleton-item">
                    <div className="skeleton-label"></div>
                    <div className="skeleton-input"></div>
                  </div>
                  <div className="skeleton-item">
                    <div className="skeleton-label"></div>
                    <div className="skeleton-input"></div>
                  </div>
                  <div className="skeleton-item">
                    <div className="skeleton-label"></div>
                    <div className="skeleton-input"></div>
                  </div>
                  <div className="skeleton-item">
                    <div className="skeleton-label"></div>
                    <div className="skeleton-checkboxes">
                      <div className="skeleton-checkbox"></div>
                      <div className="skeleton-checkbox"></div>
                      <div className="skeleton-checkbox"></div>
                      <div className="skeleton-checkbox"></div>
                    </div>
                  </div>
                </div>
              )}
              <div
                ref={aiSettingsContainerRef}
                className="ai-settings-container"
                style={{ display: aiSettingsLoaded ? 'block' : 'none' }}
              />
            </div>
            <hr className="settings-divider" />
          </React.Fragment>
        )}

        <div className="settings-section">
          <h3 className="settings-header-title">
            {formatMessage('Sync Progress')}
          </h3>
          <div className="sync-progress-container">
            <Button
              id="booktools_progress_button"
              variant="secondary"
              className="btn-myed btn-default-myed"
              onClick={handleSyncProgress}
              disabled={syncInProgress}
              aria-label={formatMessage('Update progress button')}
              title={formatMessage('Click to manually update progress')}
            >
              {syncStatus}
              {syncInProgress && (
                <span className="ml-2">
                  <i className="fa fa-spinner fa-spin" aria-hidden="true"></i>
                </span>
              )}
            </Button>
            <p className="form-text mt-2 text-muted text-p-margin-removed">
              {formatMessage(
                'Use this button to manually update your progress. (Your progress is updated automatically at regular intervals.)'
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

DrawerSettings.propTypes = {
  coursenum: PropTypes.string,
  userEmail: PropTypes.string,
  assistantEnabled: PropTypes.bool,
  userId: PropTypes.string,
}

// Add skeleton loader styles
if (typeof document !== 'undefined') {
  const styleId = 'ai-settings-skeleton-styles'
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style')
    style.id = styleId
    style.textContent = `
      .ai-settings-skeleton {
        padding: 0;
      }

      .ai-settings-skeleton .skeleton-item {
        margin-bottom: 1rem;
      }

      .ai-settings-skeleton .skeleton-label {
        width: 40%;
        height: 14px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 4px;
        margin-bottom: 0.5rem;
      }

      .ai-settings-skeleton .skeleton-input {
        width: 100%;
        height: 38px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 4px;
      }

      .ai-settings-skeleton .skeleton-checkboxes {
        display: flex;
        flex-direction: column;
        gap: 0.625rem;
      }

      .ai-settings-skeleton .skeleton-checkbox {
        width: 100%;
        height: 45px;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        animation: skeleton-loading 1.5s infinite;
        border-radius: 4px;
      }

      @keyframes skeleton-loading {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
    `
    document.head.appendChild(style)
  }
}

export default DrawerSettings
